import { Placement } from '@floating-ui/react';
import classNames from 'classnames';
import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CURRENT_USER_QUERY_KEY } from '@/modules/auth/queries/user.tsx';
import {
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { useCurrentUserEntitiesQuery } from '@/modules/entity/queries/entity.tsx';
import EditEntityModal from '@/modules/investor-dashboard/components/modals/EditEntityModal.tsx';
import { DropdownMenu, DropdownMenuItem, ModalProviderContext } from 'ui';
import { CheckIcon, ChevronRightGrayIcon, SettingsIcon } from 'ui/icons';
import { EntityType } from 'api';

export interface ProfileEntityMenuProps {
  placement?: Placement;
  entity_type?: EntityType;
}

export const ProfileEntityMenu = ({
  entity_type,
  placement = 'right-start',
}: ProfileEntityMenuProps) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const { data: entitiesData, isLoading: isEntitiesLoading } = useCurrentUserEntitiesQuery({
    page: 1,
    page_size: 100,
  });

  // const { data: entitiesSubscriptionsData, isLoading: isEntitiesSubscriptionsLoading } =
  //   useBatchEntitiesFirstActivityListQuery(
  //     { type: ActivitiesType.Subscription },
  //     (entitiesData?.pages || []).flatMap(({ data }) => data).map(({ id }) => id),
  //     !entitiesData,
  //   );

  const items = useMemo<DropdownMenuItem[]>(() => {
    if (!entitiesData?.pages) return [];
    return (entitiesData?.pages || [])
      .flatMap(({ data }) => data)
      .map(entity => {
        const { entity_name, id, type, entity_id, registers } = entity;
        const isEntityActive = params.get(ENTITY_ID_SEARCH_PARAM_KEY) === String(id);
        const hasRegisters = (registers?.length ?? 0) > 0;
        // const hasSubscriptions = !!entitiesSubscriptionsData?.[id]?.length;

        const disableReason = !hasRegisters // || !hasSubscriptions
          ? `This entity does not participate in any registry, please ask your issuer to send you an invite link and then go by this link selecting this entity to join this registry`
          : undefined;

        const disabled = !!disableReason;
        return {
          value: String(id),
          label: (
            <div className="flex justify-between w-full">
              <span className="flex gap-3 items-center">
                <span
                  className={classNames('text-ellipsis overflow-hidden', {
                    'group-hover:text-blue-700': !isEntityActive && !disabled,
                  })}
                >
                  {entity_name}
                </span>
                {isEntityActive ? <img src={CheckIcon} alt="active" /> : <span></span>}
              </span>
              <img
                alt="settings"
                src={SettingsIcon}
                onClick={e => {
                  e.stopPropagation();
                  openModal(EditEntityModal, { entity });
                }}
                className={classNames({ 'cursor-pointer hover:opacity-80': !disabled })}
              />
            </div>
          ),
          hint: disableReason,
          disabled,
          onClick: () => {
            if (isEntityActive || disabled) return;

            params.set(ENTITY_ID_SEARCH_PARAM_KEY, String(id));
            params.set(REGISTER_ID_SEARCH_PARAM_KEY, 'all');
            setParams(params, { replace: false });
            navigate(
              { search: params.toString() },
              {
                replace: false,
              },
            );

            const currentUser = queryClient.getQueryData([CURRENT_USER_QUERY_KEY]);

            queryClient.clear();
            queryClient.setQueryData([CURRENT_USER_QUERY_KEY], currentUser);
          },
          description: (
            <div className="w-full flex items-center text-[12px] leading-[20px] text-gray-500 ">
              <span>{`${type} | ${entity_id}`}</span>
            </div>
          ),
        };
      });
  }, [entitiesData?.pages, params, openModal, setParams, navigate]);

  return (
    <DropdownMenu
      loading={isEntitiesLoading} // || isEntitiesSubscriptionsLoading}
      placement={placement}
      items={items}
      itemClassName="min-w-max px-0 pl-4 pr-2"
      className="ml-2 max-h-[177px] overflow-y-auto py-1 max-w-[300px] min-w-max"
      button={
        <button className="w-full flex items-center justify-between px-4 py-[10px]">
          <p className="text-[14px] leading-[20px] text-gray-900">Entity</p>
          <div className="flex items-center space-x-2">
            <p className="text-[14px] leading-[20px] text-gray-500 capitalize">
              {entity_type ?? ''}
            </p>
            <img src={ChevronRightGrayIcon} alt="Loading..." className="w-[16px] h-[16px]" />
          </div>
        </button>
      }
    />
  );
};
